import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Confetti } from '@fluent/ui';
import { playSfx } from './sounds.js';
import './LevelUpModal.css';

export interface LevelUpModalProps {
  /** The new level the user just reached. */
  level: number;
  /** Tapping the CTA closes this and lets the parent decide next nav. */
  onContinue: () => void;
}

/**
 * LevelUpModal — full-screen celebration after a lesson completion that
 * crossed a 500-XP boundary. Sits ON TOP of the lesson-complete celebration
 * (which has already played its trophy + confetti). Two pieces of feedback:
 *   • Big level number with an XP-gold gradient pop
 *   • Confetti burst on mount, complete-fanfare SFX
 *
 * Reduced-motion safe — confetti is gated on the same media query the
 * Celebration primitive uses.
 */
export function LevelUpModal({ level, onContinue }: LevelUpModalProps) {
  const { t } = useTranslation();
  const [confettiTrigger, setConfettiTrigger] = useState(0);

  useEffect(() => {
    setConfettiTrigger((n) => n + 1);
    playSfx('complete');
  }, []);

  return (
    <div
      className="fluent-levelup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fluent-levelup-title"
    >
      <Confetti
        trigger={confettiTrigger}
        count={120}
        originY={0.4}
      />
      <div className="fluent-levelup__inner">
        <p className="fluent-levelup__kicker">{t('level_up.kicker')}</p>
        <div className="fluent-levelup__badge" aria-hidden="true">
          <span className="fluent-levelup__badge-num">{level}</span>
          <span className="fluent-levelup__badge-label">{t('level_up.level_label')}</span>
        </div>
        <h2 id="fluent-levelup-title" className="fluent-levelup__title">
          {t('level_up.title', { level })}
        </h2>
        <p className="fluent-levelup__sub">{t('level_up.subtitle')}</p>
        <Button variant="primary" size="lg" fullWidth onClick={onContinue}>
          {t('level_up.continue')}
        </Button>
      </div>
    </div>
  );
}
